import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./SortableCategoryList.scss";

/**
 * Maps a drag-end event's active/over ids to indices within the sortable guid list.
 * Returns null when the drop should be ignored (no movement, or unknown ids).
 */
export const resolveReorder = (itemGuids: string[], activeId: string, overId?: string): { oldIndex: number; newIndex: number } | null => {
    if (!overId || activeId === overId) {
        return null;
    }

    const oldIndex = itemGuids.indexOf(activeId);
    const newIndex = itemGuids.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) {
        return null;
    }

    return { oldIndex, newIndex };
};

type ListProps = {
    // Saved sibling guids in their current display order
    itemGuids: string[];
    disabled?: boolean;
    onReorder: (oldIndex: number, newIndex: number) => void;
    children: React.ReactNode;
};

const SortableCategoryList: React.FC<ListProps> = ({ itemGuids, disabled, onReorder, children }) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const result = resolveReorder(itemGuids, active.id as string, over?.id as string | undefined);

        if (result) {
            onReorder(result.oldIndex, result.newIndex);
        }
    };

    if (disabled) {
        return <>{children}</>;
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={itemGuids} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
};

type ItemProps = {
    guid: string;
    disabled?: boolean;
    children: React.ReactNode;
};

export const SortableCategoryItem: React.FC<ItemProps> = ({ guid, disabled, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: guid, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className={`SortableCategoryItem d-flex align-items-start ${isDragging ? "is-dragging" : ""}`}>
            {!disabled && (
                <div {...attributes} {...listeners} className="p-2 drag-handle" style={{ cursor: "grab" }}>
                    <i className="fas fa-grip-vertical text-muted" />
                </div>
            )}
            <div className="flex-grow-1 min-width-0">{children}</div>
        </div>
    );
};

export default SortableCategoryList;
